import React, { useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { theme } from '../../theme';
import DateField from '../shared/DateField';

// ============================================================================
// PaymentGraphGF — « Bond Graph »
// ----------------------------------------------------------------------------
// Onglet réservé aux profils Arcus. Calcule l'exposition théorique (le risque)
// et la prime annuelle théorique à partir du « Prévisionnel financier » saisi
// dans l'onglet Collecte des informations, et permet de générer la cotation
// (projet d'assurance) puis les appels de prime de variation.
//
//   Entrées (collecteGF › Prévisionnel financier) :
//     pct_acompte        → acompte %  (solde % = 1 - acompte)
//     mois_reservation   → décalage acompte (réservation à l'avance)
//     mois_solde         → décalage solde (mois avant départ)
//     previsionnel[]     → { periode, montant } → colonne C « Bookings departing »
//
//   Déclarations (surveillance › periods) :
//     realise mensuel déclaré par le client → colonne « Declarations »
//     séparateur de période + bouton « Créer une variation » si réalisé > prévu
//
//   Saisies admin (data, persistées via payment_graph_gf) :
//     taux, bondLevels[12], underlyingPct/peakPct/peak2Pct/peak3Pct,
//     cotation + cotationPeriods, variations[]
// ============================================================================

const TAX_RATE = 0.09; // taxe d'assurance (cohérent avec TarificationGF)
const N_MONTHS = 24;   // 12 mois de garantie + 12 mois de projection (année 2)

// ── Formatage ────────────────────────────────────────────────────────────
const fmt0 = n => (n == null || isNaN(n)) ? '—'
  : new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n) + ' €';
const fmt2 = n => (n == null || isNaN(n)) ? '—'
  : new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' €';

function monthLabel(startYYYYMM, offset, lang) {
  if (!startYYYYMM || !String(startYYYYMM).includes('-')) return `M${offset + 1}`;
  const [y, m] = String(startYYYYMM).split('-').map(Number);
  if (isNaN(y) || isNaN(m)) return `M${offset + 1}`;
  const d = new Date(y, m - 1 + offset, 1);
  const s = d.toLocaleDateString(lang || 'fr', { month: 'short', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}
function ymAt(startYYYYMM, offset) {
  if (!startYYYYMM || !String(startYYYYMM).includes('-')) return null;
  const [y, m] = String(startYYYYMM).split('-').map(Number);
  if (isNaN(y) || isNaN(m)) return null;
  const d = new Date(y, m - 1 + offset, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
// Dates d'une période bond level [startOff..endOff] (offsets de mois depuis le 1er mois).
// date_debut = 1er jour du mois de début ; date_fin = dernier jour du mois de fin.
function periodDates(startYYYYMM, startOff, endOff) {
  if (!startYYYYMM || !String(startYYYYMM).includes('-')) return { date_debut: '', date_fin: '' };
  const [y, m] = String(startYYYYMM).split('-').map(Number);
  const dDeb = new Date(y, m - 1 + startOff, 1);
  const dFin = new Date(y, m - 1 + endOff + 1, 0); // jour 0 du mois suivant = dernier jour du mois
  const iso = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { date_debut: iso(dDeb), date_fin: iso(dFin) };
}

// ── Styles locaux ────────────────────────────────────────────────────────
const card = {
  background: theme.surface, borderRadius: theme.radiusLg,
  border: `1.5px solid ${theme.borderStrong}`, padding: 18, marginBottom: 16,
};
const cardTitle = { fontSize: 13, fontWeight: 700, color: theme.text, marginBottom: 12,
  display: 'flex', alignItems: 'center', gap: 8, textTransform: 'uppercase', letterSpacing: '.04em' };
const smallLbl = { fontSize: 11, color: theme.textSecondary, fontWeight: 600, display: 'block', marginBottom: 3 };
const numInp = {
  border: `1px solid ${theme.border}`, borderRadius: 6, padding: '6px 8px',
  fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit', width: '100%', textAlign: 'right',
};
const lockedInp = { ...numInp, background: '#F8FAFC', color: theme.textTertiary, border: `1px solid #F1F5F9` };
const th = { textAlign: 'right', padding: '8px 10px', fontSize: 10.5, fontWeight: 700,
  color: theme.textTertiary, textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap' };
const td = { padding: '6px 10px', fontSize: 12.5, textAlign: 'right', whiteSpace: 'nowrap' };
// Zone parallèle « déclarations » : teinte + séparateur visuel
const PAR_TINT = '#F0F7FC';
const PAR_BORDER = '3px solid #93C5FD';

// ── Calcul de prime par période (partagé cotation / variations) ──────────
// Prime HT = exposition × taux% × (jours de la période / 365), bornes incluses.
// (Cohérent avec la formule de variation validée : 24000 × 3,2% × 184/365 = 387,16.)
function daysInclusive(d1, d2) {
  if (!d1 || !d2) return 0;
  const a = new Date(d1), b = new Date(d2);
  if (isNaN(a) || isNaN(b)) return 0;
  return Math.round((b - a) / 86400000) + 1;
}
let CUR_LANG = 'fr';
const DATE_LOCALES = { fr: 'fr-FR', en: 'en-GB', de: 'de-DE', sv: 'sv-SE' };
function fmtDMY(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso || '';
  const dt = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return dt.toLocaleDateString(DATE_LOCALES[CUR_LANG] || 'fr-FR', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

// ── Carte repliable : en-tête cliquable + résumé toujours visible ─────────
function CollapsibleCard({ icon, title, summary, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: theme.radiusLg, marginBottom: 14, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', cursor: 'pointer', userSelect: 'none', background: open ? '#fff' : theme.surfaceAlt }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={theme.textTertiary} strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s', flexShrink: 0 }}>
          <path d="M9 6l6 6l-6 6" />
        </svg>
        {icon}
        <span style={{ fontSize: 13, fontWeight: 700, color: theme.text, textTransform: 'uppercase', letterSpacing: '.03em' }}>{title}</span>
        {summary && <span style={{ marginLeft: 'auto', fontSize: 12.5, color: theme.textSecondary, fontWeight: 600, textAlign: 'right' }}>{summary}</span>}
      </div>
      {open && <div style={{ padding: '2px 16px 16px' }}>{children}</div>}
    </div>
  );
}

// ── Graphique « Bond graph » (SVG pur, mêmes séries que l'Excel) ──────────
// Séries : Exposure (F) · Last year Bond level (G) · Bond level (H)
// + Updated exposure (pointillés) quand des déclarations validées existent.
function BondChart({ F, FUpd, bonds, lastBond, startMonth, lang, showUpd, t }) {
  const N = Math.min(24, F.length);
  const toNum = v => (v === '' || v == null) ? null : (isNaN(parseFloat(v)) ? null : parseFloat(v));
  const sExpo = Array.from({ length: N }, (_, i) => toNum(F[i]));
  const sUpd  = showUpd ? Array.from({ length: N }, (_, i) => toNum(FUpd[i])) : null;
  const sBond = Array.from({ length: N }, (_, i) => (i < 12 ? toNum(bonds[i]) : null));
  const sLast = Array.from({ length: N }, (_, i) => toNum(lastBond[i]));
  const all = [...sExpo, ...(sUpd || []), ...sBond, ...sLast].filter(v => v != null && v > 0);
  if (!all.length) return null;
  // Échelle Y « propre » : arrondie au demi-ordre de grandeur supérieur.
  const rawMax = Math.max(...all);
  const p = Math.pow(10, Math.floor(Math.log10(rawMax)));
  const maxV = Math.ceil(rawMax / (p / 2)) * (p / 2);
  const W = 980, H = 322, padL = 70, padR = 16, padT = 16, padB = 58;
  const x = i => padL + (W - padL - padR) * (N <= 1 ? 0 : i / (N - 1));
  const y = v => padT + (H - padT - padB) * (1 - v / maxV);
  const path = arr => {
    let d = '', pen = false;
    arr.forEach((v, i) => {
      if (v == null) { pen = false; return; }
      d += (pen ? ' L' : ' M') + x(i).toFixed(1) + ' ' + y(v).toFixed(1);
      pen = true;
    });
    return d.trim();
  };
  const fmtC = v => v >= 1e6
    ? (v / 1e6).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US', { maximumFractionDigits: 1 }) + ' M€'
    : Math.round(v / 1e3).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US') + ' k€';
  const SERIES = [
    { d: path(sExpo), color: '#2563EB', dash: null,  label: t('gf.pg_col_exposure') },
    ...(sBond.some(v => v != null) ? [{ d: path(sBond), color: '#F18A00', dash: null, label: t('gf.pg_col_bond') }] : []),
    ...(sLast.some(v => v != null) ? [{ d: path(sLast), color: '#94A3B8', dash: '5 4', label: t('gf.pg_col_last_bond') }] : []),
    ...(sUpd && sUpd.some(v => v != null) ? [{ d: path(sUpd), color: '#0369A1', dash: '3 4', label: t('gf.pg_col_updated_exposure') }] : []),
  ];
  return (
    <div>
      {/* Légende */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
        {SERIES.map((s, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: theme.textSecondary, fontWeight: 600 }}>
            <svg width="22" height="8"><line x1="0" y1="4" x2="22" y2="4" stroke={s.color} strokeWidth="2.5" strokeDasharray={s.dash || 'none'} /></svg>
            {s.label}
          </span>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img">
        {/* Grille + axe Y */}
        {[0, .25, .5, .75, 1].map(f => {
          const v = f * maxV, yy = y(v);
          return (
            <g key={f}>
              <line x1={padL} y1={yy} x2={W - padR} y2={yy} stroke={theme.border} strokeWidth="1" />
              <text x={padL - 8} y={yy + 3.5} textAnchor="end" fontSize="10.5" fill={theme.textTertiary}>{v > 0 ? fmtC(v) : '0'}</text>
            </g>
          );
        })}
        {/* Étiquettes X — TOUS les mois, inclinées (repère + libellé court) */}
        {Array.from({ length: N }, (_, i) => i).map(i => {
          const short = monthLabel(startMonth, i, lang).replace(/ 20(\d{2})$/, ' $1'); // « Avr. 2026 » → « Avr. 26 »
          return (
            <g key={i}>
              <line x1={x(i)} y1={H - padB} x2={x(i)} y2={H - padB + 4} stroke={theme.borderStrong} strokeWidth="1" />
              <text x={x(i)} y={H - padB + 8} fontSize="9.5" fill={theme.textTertiary}
                transform={`rotate(-50 ${x(i)} ${H - padB + 8})`} textAnchor="end">
                {short}
              </text>
            </g>
          );
        })}
        {/* Séparateur année 1 / année 2 */}
        {N > 12 && <line x1={x(11.5)} y1={padT} x2={x(11.5)} y2={H - padB} stroke={theme.borderStrong} strokeWidth="1" strokeDasharray="2 4" />}
        {/* Courbes */}
        {SERIES.map((s, i) => (
          <path key={i} d={s.d} fill="none" stroke={s.color} strokeWidth="2.25" strokeDasharray={s.dash || 'none'} strokeLinejoin="round" strokeLinecap="round" />
        ))}
      </svg>
    </div>
  );
}
function calcPeriodPrime(p) {
  const expo = parseFloat(p.exposition) || 0;
  const tx   = parseFloat(p.taux) || 0;
  const days = daysInclusive(p.date_debut, p.date_fin);
  const prorata = days > 0 ? days / 365 : 0;
  const ht   = expo * tx / 100 * prorata;
  const taxe = ht * TAX_RATE;
  return { ht, taxe, ttc: ht + taxe };
}
function periodsTotals(periods) {
  return (periods || []).reduce((a, p) => {
    const { ht, taxe, ttc } = calcPeriodPrime(p);
    return { ht: a.ht + ht, taxe: a.taxe + taxe, ttc: a.ttc + ttc };
  }, { ht: 0, taxe: 0, ttc: 0 });
}

// ── Modèle bond graph : Cash Received / Cash Held / Exposure à partir d'une
// source de volume mensuel Cval(i). Utilisé pour le prévisionnel ET pour le
// réalisé déclaré (colonne « Updated exposure »). Logique identique, validée.
function computeBondModel(Cval, acompte, solde, reserv, soldeM) {
  const Cfull = Array.from({ length: N_MONTHS }, (_, i) => Cval(i));
  const B = new Array(N_MONTHS);
  let seedDep = 0; for (let k = 0; k < reserv; k++) seedDep += Cval(k);
  let seedBal = 0; for (let k = 0; k < soldeM; k++) seedBal += Cval(k);
  B[0] = acompte * Cval(reserv) + solde * Cval(soldeM) + acompte * seedDep + solde * seedBal;
  for (let m = 1; m < N_MONTHS; m++) B[m] = acompte * Cval(m + reserv) + solde * Cval(m + soldeM);
  const D = new Array(N_MONTHS);
  D[0] = Math.max(0, B[0] - Cfull[0]);
  for (let m = 1; m < N_MONTHS; m++) D[m] = Math.max(0, D[m - 1] + B[m] - Cfull[m]);
  const F = Cfull.map((cc, m) => Math.max(cc, D[m]));
  return { Cfull, B, D, F };
}

// ── Calculateur de primes réutilisable (périodes date/expo/taux → prime) ──
function PeriodsCalculator({ periods, onChange, readOnly, t, minWidth = 720 }) {
  const list = Array.isArray(periods) ? periods : [];
  const add = () => onChange([...list, { id: Date.now(), date_debut: '', date_fin: '', exposition: '', taux: '' }]);
  const del = id => onChange(list.filter(p => p.id !== id));
  const upd = (id, f, v) => onChange(list.map(p => p.id === id ? { ...p, [f]: v } : p));
  const totals = periodsTotals(list);
  const anyFilled = list.some(p => p.exposition || p.taux);

  return (
    <div>
      {list.length > 0 && (
        <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 10 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth }}>
              <thead>
                <tr style={{ background: theme.surfaceAlt, borderBottom: `1px solid ${theme.border}` }}>
                  <th style={{ ...th, textAlign: 'center', width: 30 }}>#</th>
                  <th style={{ ...th, textAlign: 'left' }}>{t('gf.tarif_date_debut')}</th>
                  <th style={{ ...th, textAlign: 'left' }}>{t('gf.tarif_date_fin')}</th>
                  <th style={th}>{t('gf.tarif_exposition')}</th>
                  <th style={th}>{t('gf.tarif_taux')}</th>
                  <th style={th}>{t('gf.tarif_prime_ht')}</th>
                  <th style={th}>{t('gf.tarif_taxe')}</th>
                  <th style={th}>{t('gf.tarif_prime_ttc')}</th>
                  <th style={{ ...th, width: 30 }} />
                </tr>
              </thead>
              <tbody>
                {list.map((p, idx) => {
                  const { ht, taxe, ttc } = calcPeriodPrime(p);
                  const filled = p.exposition || p.taux;
                  return (
                    <tr key={p.id} style={{ borderTop: `1px solid ${theme.border}`, background: idx % 2 === 0 ? '#fff' : '#FCFCFB' }}>
                      <td style={{ ...td, textAlign: 'center', color: theme.textTertiary, fontWeight: 700 }}>{idx + 1}</td>
                      <td style={{ padding: '5px 6px' }}>
                        {readOnly
                          ? <div style={{ ...lockedInp, textAlign: 'left' }}>{fmtDMY(p.date_debut) || '\u2014'}</div>
                          : <DateField value={p.date_debut || ''} onChange={v => upd(p.id, 'date_debut', v)} style={{ ...numInp, textAlign: 'left' }} />}
                      </td>
                      <td style={{ padding: '5px 6px' }}>
                        {readOnly
                          ? <div style={{ ...lockedInp, textAlign: 'left' }}>{fmtDMY(p.date_fin) || '\u2014'}</div>
                          : <DateField value={p.date_fin || ''} onChange={v => upd(p.id, 'date_fin', v)} style={{ ...numInp, textAlign: 'left' }} />}
                      </td>
                      <td style={{ padding: '5px 6px' }}>
                        <div style={{ position: 'relative' }}>
                          <input type="number" value={p.exposition || ''} readOnly={readOnly} min="0" step="1"
                            onChange={e => upd(p.id, 'exposition', e.target.value)} placeholder="0"
                            style={{ ...(readOnly ? lockedInp : numInp), paddingRight: 18 }} />
                          <span style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: theme.textTertiary, pointerEvents: 'none' }}>€</span>
                        </div>
                      </td>
                      <td style={{ padding: '5px 6px' }}>
                        <div style={{ position: 'relative' }}>
                          <input type="number" value={p.taux || ''} readOnly={readOnly} min="0" step="0.0001" max="100"
                            onChange={e => upd(p.id, 'taux', e.target.value)} placeholder="0"
                            style={{ ...(readOnly ? lockedInp : numInp), paddingRight: 18 }} />
                          <span style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: theme.textTertiary, pointerEvents: 'none' }}>%</span>
                        </div>
                      </td>
                      <td style={{ ...td, color: filled ? theme.text : theme.textTertiary }}>{filled ? fmt2(ht) : '—'}</td>
                      <td style={{ ...td, color: filled ? theme.textSecondary : theme.textTertiary }}>{filled ? fmt2(taxe) : '—'}</td>
                      <td style={{ ...td, fontWeight: 700, color: filled ? '#059669' : theme.textTertiary }}>{filled ? fmt2(ttc) : '—'}</td>
                      <td style={{ textAlign: 'center' }}>
                        {!readOnly && (
                          <button onClick={() => del(p.id)} title="✕"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', fontSize: 15, lineHeight: 1, opacity: .7 }}>✕</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {list.length > 1 && anyFilled && (
                <tfoot>
                  <tr style={{ borderTop: `2px solid ${theme.borderStrong}`, background: '#F0FDF4' }}>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 800, color: '#065F46' }} colSpan={5}>{t('gf.tarif_total')}</td>
                    <td style={{ ...td, fontWeight: 700, color: '#065F46' }}>{fmt2(totals.ht)}</td>
                    <td style={{ ...td, color: '#065F46' }}>{fmt2(totals.taxe)}</td>
                    <td style={{ ...td, fontWeight: 800, color: '#059669' }}>{fmt2(totals.ttc)}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
      {!readOnly && (
        <button onClick={add}
          style={{ fontSize: 13, color: '#2563EB', background: 'none', border: '1px dashed #93C5FD', borderRadius: 7, padding: '9px', cursor: 'pointer', width: '100%', fontWeight: 600 }}>
          {t('gf.tarif_add_period')}
        </button>
      )}
      {list.length === 0 && readOnly && (
        <div style={{ textAlign: 'center', color: theme.textTertiary, fontSize: 12.5, padding: '18px 0' }}>{t('gf.pg_cotation_empty')}</div>
      )}
    </div>
  );
}

export default function PaymentGraphGF({ collecteGF, data, onChange, onCommit, surveillance, readOnly, subscribed }) {
  const { t, lang } = useLanguage();
  CUR_LANG = lang;
  const cfg = data || {};
  const c = collecteGF || {};
  // Une fois la police souscrite, le bond graph / la cotation se figent ;
  // les variations, elles, restent modifiables (readOnly = accounts/archivé/résilié).
  const cotationLocked = readOnly || subscribed;

  // ── Paramètres issus de la collecte ─────────────────────────────────────
  const acompte = (parseFloat(c.pct_acompte) || 0) / 100;     // 0.30
  const solde   = Math.max(0, 1 - acompte);                   // 0.70
  const reserv  = Math.max(0, parseInt(c.mois_reservation, 10) || 0); // décalage acompte
  const soldeM  = Math.max(0, parseInt(c.mois_solde, 10) || 0);       // décalage solde
  const prev    = Array.isArray(c.previsionnel) ? c.previsionnel : [];

  // Volume d'affaires mensuel (année 1) — colonne C, 12 mois
  const C1 = useMemo(
    () => Array.from({ length: 12 }, (_, i) => parseFloat(prev[i]?.montant) || 0),
    [prev]
  );
  const startMonth = prev[0]?.periode || '';
  const hasData = C1.some(v => v > 0);

  // ── Modèle de calcul (24 mois, C répété avec période 12) ────────────────
  // C[i] = volume prévisionnel du mois i (le prévisionnel se répète tous les 12 mois).
  const model = useMemo(
    () => computeBondModel(i => C1[((i % 12) + 12) % 12], acompte, solde, reserv, soldeM),
    [C1, acompte, solde, reserv, soldeM]
  );

  // ── Prime (année 1 uniquement) ──────────────────────────────────────────
  const taux = parseFloat(cfg.taux) || 0; // en %
  // ── Bond level par périodes ─────────────────────────────────────────────
  // Périodes contiguës démarrant au 1er mois : [{ start, end, amount }] (offsets 0..11 inclus).
  // Chaque mois d'une période hérite du même bond level.
  // Rétrocompat : si aucune bondPeriods mais un ancien bondLevels[] manuel, on l'affiche.
  const bondPeriods = Array.isArray(cfg.bondPeriods) ? cfg.bondPeriods : [];
  const bondLevels = Array.from({ length: 12 }, (_, i) => {
    const p = bondPeriods.find(pp => i >= pp.start && i <= pp.end);
    if (p) return p.amount;
    if (!bondPeriods.length && cfg.bondLevels && cfg.bondLevels[i] != null) return cfg.bondLevels[i];
    return '';
  });
  const bondCoveredUntil = bondPeriods.reduce((mx, p) => Math.max(mx, p.end + 1), 0); // 1er mois non couvert
  const nextBondMonth = bondCoveredUntil < 12 ? bondCoveredUntil : -1;
  const lastYearBond = cfg.lastYearBond || []; // rempli au renouvellement (expo N-1)

  const premiumMonthly = bondLevels.map(b => (parseFloat(b) || 0) * (taux / 100) / 12);
  const annualHT  = premiumMonthly.reduce((a, b) => a + b, 0);
  const annualTTC = annualHT * (1 + TAX_RATE);
  // Frais de gestion : montant fixe ajouté au Total gross premium (une seule fois).
  const fraisGestion = Math.max(0, parseFloat(cfg.frais_gestion) || 0);
  const annualTTCTotal = annualTTC + fraisGestion;

  // ── Totaux ──────────────────────────────────────────────────────────────
  const totalBookings1 = C1.reduce((a, b) => a + b, 0);       // = C27 dans l'Excel
  const peakExposure1  = Math.max(0, ...model.F.slice(0, 12));

  // ── Setters ─────────────────────────────────────────────────────────────
  const setField = (k, v) => onChange({ ...cfg, [k]: v });
  const addBondPeriod = (end, amount) => {
    const start = bondCoveredUntil;
    if (start > 11 || end < start || end > 11) return;
    onChange({ ...cfg, bondPeriods: [...bondPeriods, { start, end, amount: parseFloat(amount) || 0 }] });
  };
  const removeLastBondPeriod = () => onChange({ ...cfg, bondPeriods: bondPeriods.slice(0, -1) });
  const resetBondPeriods = () => onChange({ ...cfg, bondPeriods: [] });
  const genDate = iso => { try { return new Date(iso).toLocaleDateString(DATE_LOCALES[lang] || 'fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return ''; } };

  // Modale « Définir bond level » (période démarrant au 1er mois non couvert).
  const [bondModal, setBondModal] = useState(null); // { end, amount, expoMax }
  const openBondModal = () => setBondModal({ end: nextBondMonth, amount: '', expoMax: false });
  const confirmBondModal = () => {
    if (!bondModal) return;
    const amt = bondModal.expoMax ? peakExposure1 : (parseFloat(bondModal.amount) || 0);
    addBondPeriod(bondModal.end, amt);
    setBondModal(null);
  };

  // ── Cotation (générée automatiquement depuis les périodes bond level) ─────
  // Chaque période bond level → période de cotation (dates + exposition = bond level + taux).
  const cotationPeriods = bondPeriods.map((p, i) => {
    const { date_debut, date_fin } = periodDates(startMonth, p.start, p.end);
    return { id: `bp${i}`, date_debut, date_fin, exposition: p.amount, taux };
  });
  const cotationTot = periodsTotals(cotationPeriods);
  const cotationFullyCovered = nextBondMonth === -1 && bondPeriods.length > 0;
  const canGenerateCotation = cotationFullyCovered && taux > 0;
  const cotation = cfg.cotation || null;
  const [showCotationConfirm, setShowCotationConfirm] = useState(false);
  const doGenerateCotation = () => {
    const newData = { ...cfg, cotation: {
      generated_at: new Date().toISOString(),
      total_ttc: cotationTot.ttc + fraisGestion, total_ht: cotationTot.ht, frais_gestion: fraisGestion, nb_periods: cotationPeriods.length,
      periods: cotationPeriods.map(p => ({ date_debut: p.date_debut, date_fin: p.date_fin, exposition: p.exposition, taux: p.taux })),
    } };
    if (onCommit) onCommit(newData); else onChange(newData);
    setShowCotationConfirm(false);
  };

  // ── Déclarations client (surveillance) ───────────────────────────────────
  const surv = surveillance || {};
  const survPeriods = Array.isArray(surv.periods) ? surv.periods : [];
  // Seules les déclarations VALIDÉES par l'admin alimentent le Payment Graph.
  const declaredPeriods = survPeriods.filter(p => p && p.validated_at);
  const realiseByMonth = {};
  declaredPeriods.forEach(p => (Array.isArray(p.lines) ? p.lines : []).forEach(l => {
    if (l && l.realise != null && l.realise !== '') realiseByMonth[l.month] = parseFloat(l.realise);
  }));
  const periodByEndMonth = {};
  declaredPeriods.forEach(p => {
    const lines = Array.isArray(p.lines) ? p.lines : [];
    const totalReal = lines.reduce((s, l) => s + (parseFloat(l.realise) || 0), 0);
    const totalPrev = lines.reduce((s, l) => s + (parseFloat(l.previsionnel) || 0), 0);
    if (p.end_month) periodByEndMonth[p.end_month] = { ...p, totalReal, totalPrev, over: totalReal > totalPrev };
  });
  const totalRealise1 = declaredPeriods.reduce((s, p) =>
    s + (Array.isArray(p.lines) ? p.lines.reduce((a, l) => a + (parseFloat(l.realise) || 0), 0) : 0), 0);
  const hasDeclarations = Object.keys(realiseByMonth).length > 0;

  // ── Tableau parallèle « déclarations » ───────────────────────────────────
  // Exposition actualisée : même modèle, mais avec le réalisé déclaré là où il
  // existe (sinon prévisionnel). IMPORTANT : l'année 2 (i≥12) reprend les valeurs
  // de l'année 1 CORRESPONDANTE — donc le RÉALISÉ répété, pas le prévisionnel.
  // (Sinon Cash Received des derniers mois — Jan/Fév — est faussé, car il référence
  //  Bookings(m+3) qui tombe en année 2.)
  const modelUpd = computeBondModel(i => {
    const mm = ((i % 12) + 12) % 12;                  // mois année-1 correspondant
    const ym = ymAt(startMonth, mm);
    const r = (ym != null) ? realiseByMonth[ym] : undefined;
    return (r != null) ? r : C1[mm];
  }, acompte, solde, reserv, soldeM);
  const peakUpdated = Math.max(0, ...modelUpd.F.slice(0, 12));

  // ── Variations de prime (calculées automatiquement) ──────────────────────
  // Booking Departing : total initial vs total après déclarations validées (jamais à la baisse).
  const declaredTotalBooking = C1.reduce((sum, c, m) => {
    const ym = ymAt(startMonth, m);
    const r = (ym != null) ? realiseByMonth[ym] : undefined;
    return sum + (r != null ? r : c);
  }, 0);
  const newBookingTotal = Math.max(totalBookings1, declaredTotalBooking);
  const bookingIncrease = newBookingTotal - totalBookings1;

  // Peak bond % par période bond level = bond level de la période / total Booking Departing initial.
  const peakBondByPeriod = bondPeriods.map(p => ({
    start: p.start, end: p.end, amount: parseFloat(p.amount) || 0,
    pct: totalBookings1 > 0 ? Math.round(((parseFloat(p.amount) || 0) / totalBookings1) * 10000) / 10000 : 0,
  }));

  // Offset (0..11) d'un mois YYYY-MM depuis le 1er mois du tableau.
  const offsetOfYm = ym => {
    if (!ym || !startMonth || !String(startMonth).includes('-')) return -1;
    const [ys, ms] = String(startMonth).split('-').map(Number);
    const [y, m] = String(ym).split('-').map(Number);
    return (y - ys) * 12 + (m - ms);
  };

  // Découpe une déclaration en sous-variations, une par période bond level traversée.
  // Chaque sous-variation : valeur = newTotal × peak bond% − bond level (jamais négatif),
  // prime = valeur × taux × jours(intersection)/365.
  function splitDeclarationIntoVariations(decl) {
    const lines = Array.isArray(decl.lines) ? decl.lines : [];
    const groups = []; // { bpIndex, months:[offset…] } — mois consécutifs par période bond level
    lines.forEach(l => {
      const off = offsetOfYm(l.month);
      const bpIdx = bondPeriods.findIndex(bp => off >= bp.start && off <= bp.end);
      if (bpIdx < 0) return;
      const last = groups[groups.length - 1];
      if (last && last.bpIndex === bpIdx) last.months.push(off);
      else groups.push({ bpIndex: bpIdx, months: [off] });
    });
    return groups.map(g => {
      const bp = bondPeriods[g.bpIndex];
      const bondLevel = parseFloat(bp.amount) || 0;
      const pct = totalBookings1 > 0 ? Math.round((bondLevel / totalBookings1) * 10000) / 10000 : 0; // peak% arrondi à 4 décimales (méthode Excel)
      // Valeur de variation arrondie à la centaine d'euros SUPÉRIEURE.
      const valeurRaw = Math.max(0, newBookingTotal * pct - bondLevel);
      const valeur = Math.ceil(valeurRaw / 100) * 100;
      const startOff = Math.min(...g.months), endOff = Math.max(...g.months);
      const { date_debut, date_fin } = periodDates(startMonth, startOff, endOff);
      const prime = calcPeriodPrime({ exposition: valeur, taux, date_debut, date_fin });
      return { bpIndex: g.bpIndex, bondLevel, pct, valeur, date_debut, date_fin,
        days: daysInclusive(date_debut, date_fin), ht: prime.ht, taxe: prime.taxe, ttc: prime.ttc };
    });
  }

  // Déclarations validées EN HAUSSE → variations (une entrée par déclaration, sous-variations par période).
  const variationDecls = declaredPeriods
    .filter(p => {
      const tReal = (p.lines || []).reduce((s, l) => s + (parseFloat(l.realise) || 0), 0);
      const tPrev = (p.lines || []).reduce((s, l) => s + (parseFloat(l.previsionnel) || 0), 0);
      return tReal > tPrev;
    })
    .map(p => {
      const subs = splitDeclarationIntoVariations(p);
      const tot = subs.reduce((a, s) => ({ ht: a.ht + s.ht, taxe: a.taxe + s.taxe, ttc: a.ttc + s.ttc }), { ht: 0, taxe: 0, ttc: 0 });
      return { decl: p, label: `${monthLabel(p.start_month, 0, lang)} → ${monthLabel(p.end_month, 0, lang)}`, subs, tot };
    });

  // ── Appels de prime de variation GLOBAUX ──────────────────────────
  // UN appel regroupe TOUTES les variations en attente (toutes déclarations
  // confondues) : si le client revoit son prévisionnel pour le reste de
  // l'année, un seul appel de prime est émis (pas un par période).
  const variationAppelsLegacy = cfg.variationAppels || {};   // rétrocompat : anciens appels par déclaration
  const variationAppelsList = Array.isArray(cfg.variationAppelsList) ? cfg.variationAppelsList : [];
  const coveredBy = declId => variationAppelsLegacy[declId]
    || variationAppelsList.find(a => (a.decl_ids || []).includes(declId)) || null;
  const pendingVarDecls = variationDecls.filter(vd => !coveredBy(vd.decl.id) && vd.tot.ttc > 0);
  const pendingTot = pendingVarDecls.reduce((a, vd) => ({ ht: a.ht + vd.tot.ht, ttc: a.ttc + vd.tot.ttc }), { ht: 0, ttc: 0 });
  const appelsHistory = [
    ...variationAppelsList,
    ...Object.values(variationAppelsLegacy).map(a => ({ ...a, nb_decls: 1 })),
  ].sort((a, b) => new Date(b.generated_at) - new Date(a.generated_at));
  const [confirmAppel, setConfirmAppel] = useState(false);
  // Sélection des variations à inclure dans l'appel (toutes cochées par défaut).
  const [appelSelection, setAppelSelection] = useState([]);
  const toggleAppelSel = id => setAppelSelection(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);
  const selectedVarDecls = pendingVarDecls.filter(vd => appelSelection.includes(vd.decl.id));
  const selectedTot = selectedVarDecls.reduce((a, vd) => ({ ht: a.ht + vd.tot.ht, ttc: a.ttc + vd.tot.ttc }), { ht: 0, ttc: 0 });
  const doGenerateAppel = () => {
    if (!selectedVarDecls.length) return;
    const appel = {
      id: Date.now(), generated_at: new Date().toISOString(),
      decl_ids: selectedVarDecls.map(vd => vd.decl.id),
      nb_decls: selectedVarDecls.length,
      nb_subs: selectedVarDecls.reduce((s, vd) => s + vd.subs.length, 0),
      total_ht: selectedTot.ht, total_ttc: selectedTot.ttc,
    };
    const newData = { ...cfg, variationAppelsList: [...variationAppelsList, appel] };
    if (onCommit) onCommit(newData); else onChange(newData);
    setConfirmAppel(false);
  };

  // ── Écran « données manquantes » ────────────────────────────────────────
  if (!hasData) {
    return (
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: theme.text, marginBottom: 4 }}>{t('gf.pg_title')}</div>
        <div style={{ fontSize: 12.5, color: theme.textSecondary, marginBottom: 18 }}>{t('gf.pg_subtitle')}</div>
        <div style={{ background: '#FFF7F2', border: '1px solid #FDD9BF', borderRadius: 10,
          padding: '18px 20px', fontSize: 13.5, color: '#9A3412', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C2410C" strokeWidth="1.75"
            strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
            <path d="M12 9v4M12 16h.01M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0 -18" />
          </svg>
          <div>{t('gf.pg_incomplete')}</div>
        </div>
      </div>
    );
  }

  const paramMissing = acompte === 0 || reserv === 0;

  const ParamPill = ({ label, value }) => (
    <div style={{ background: theme.surfaceAlt, border: `1px solid ${theme.border}`, borderRadius: 8, padding: '9px 12px' }}>
      <div style={{ fontSize: 10.5, color: theme.textTertiary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: theme.text, marginTop: 2 }}>{value}</div>
    </div>
  );

  return (
    <div>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.text }}>{t('gf.pg_title')}</div>
          <div style={{ fontSize: 12.5, color: theme.textSecondary, marginTop: 3 }}>{t('gf.pg_subtitle')}</div>
        </div>
        {/* Synthèse expo + prime */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ background: '#FEF3E2', border: '1px solid #FCD9A6', borderRadius: 10, padding: '10px 16px', textAlign: 'right' }}>
            <div style={{ fontSize: 10.5, color: '#9A5A00', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('gf.pg_peak_exposure')}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#C2410C' }}>{fmt0(peakExposure1)}</div>
          </div>
          <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 10, padding: '10px 16px', textAlign: 'right' }}>
            <div style={{ fontSize: 10.5, color: '#065F46', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('gf.pg_annual_ttc')}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#059669' }}>{fmt2(annualTTCTotal)}</div>
            <div style={{ fontSize: 10.5, color: '#065F46', marginTop: 1 }}>
              {t('gf.pg_annual_ht')} {fmt2(annualHT)}{fraisGestion > 0 ? ` · ${t('gf.pg_mgmt_fees')} ${fmt2(fraisGestion)}` : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Paramètres issus de la collecte (lecture seule) */}
      <div style={card}>
        <div style={cardTitle}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={theme.orange} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 3v4a1 1 0 0 0 1 1h4M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
          </svg>
          {t('gf.pg_params_title')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
          <ParamPill label={t('gf.pg_param_acompte')} value={`${(acompte * 100).toLocaleString('fr-FR')} %`} />
          <ParamPill label={t('gf.pg_param_solde')} value={`${(solde * 100).toLocaleString('fr-FR')} %`} />
          <ParamPill label={t('gf.pg_param_reserv')} value={`${reserv} ${t('gf.pg_months')}`} />
          <ParamPill label={t('gf.pg_param_solde_delai')} value={`${soldeM} ${t('gf.pg_months')}`} />
          <ParamPill label={t('gf.pg_total_bookings')} value={fmt0(totalBookings1)} />
        </div>
        {paramMissing && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#C2410C', display: 'flex', gap: 6, alignItems: 'center' }}>
            <span>⚠</span>{t('gf.pg_param_missing')}
          </div>
        )}
      </div>

      {/* Paramètres de calcul Arcus (éditables) */}
      <div style={card}>
        <div style={cardTitle}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={theme.orange} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
          </svg>
          {t('gf.pg_admin_title')}
        </div>

        {/* Taux de prime + frais de gestion */}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ width: 200 }}>
            <label style={smallLbl}>{t('gf.pg_rate')}</label>
            <div style={{ position: 'relative' }}>
              <input type="number" value={cfg.taux ?? ''} readOnly={cotationLocked} step="0.01" min="0"
                onChange={e => setField('taux', e.target.value)} placeholder="Ex. 1,4"
                style={{ ...(cotationLocked ? lockedInp : numInp), textAlign: 'left', paddingRight: 22 }} />
              <span style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: theme.textTertiary, pointerEvents: 'none' }}>%</span>
            </div>
          </div>
          <div style={{ width: 200 }}>
            <label style={smallLbl}>{t('gf.pg_mgmt_fees')}</label>
            <div style={{ position: 'relative' }}>
              <input type="number" value={cfg.frais_gestion ?? ''} readOnly={cotationLocked} step="1" min="0"
                onChange={e => setField('frais_gestion', e.target.value)} placeholder="Ex. 150"
                style={{ ...(cotationLocked ? lockedInp : numInp), textAlign: 'left', paddingRight: 24 }} />
              <span style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: theme.textTertiary, pointerEvents: 'none' }}>€</span>
            </div>
          </div>
        </div>
      </div>

      {/* Cotation — projet d'assurance (repliable) */}
      <CollapsibleCard
        defaultOpen={!cotation?.generated_at}
        icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={theme.orange} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M9 14c0 1.66 2.69 3 6 3s6 -1.34 6 -3M9 14c0 -1.66 2.69 -3 6 -3s6 1.34 6 3M9 14v4c0 1.66 2.69 3 6 3s6 -1.34 6 -3v-4M4 6h3M5.5 4.5v3" /></svg>}
        title={t('gf.pg_cotation_title')}
        summary={cotation?.generated_at ? `\u2713 ${genDate(cotation.generated_at)} \u00b7 ${fmt2(cotation.total_ttc)}` : (cotationPeriods.length ? fmt2(cotationTot.ttc) : null)}>
        <div style={{ fontSize: 12, color: theme.textSecondary, marginBottom: 12 }}>{t('gf.pg_cotation_auto_hint')}</div>

        {cotationPeriods.length === 0 ? (
          <div style={{ textAlign: 'center', color: theme.textTertiary, fontSize: 12.5, padding: '18px 0', border: `1px dashed ${theme.border}`, borderRadius: 8 }}>
            {t('gf.pg_cotation_need_bond')}
          </div>
        ) : (
          <>
            <PeriodsCalculator periods={cotationPeriods} onChange={() => {}} readOnly={true} t={t} />
            {fraisGestion > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, marginTop: 8, fontSize: 12.5 }}>
                <div style={{ textAlign: 'right', color: theme.textSecondary }}>
                  <div>{t('gf.pg_mgmt_fees')} : <strong style={{ color: theme.text }}>{fmt2(fraisGestion)}</strong></div>
                  <div style={{ marginTop: 3, fontSize: 13.5, fontWeight: 800, color: '#059669' }}>{t('gf.pg_annual_ttc')} : {fmt2(cotationTot.ttc + fraisGestion)}</div>
                </div>
              </div>
            )}
            {!cotationFullyCovered && (
              <div style={{ fontSize: 11.5, color: '#C2410C', display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
                <span>⚠</span>{t('gf.pg_cotation_incomplete')}
              </div>
            )}
          </>
        )}

        {/* Générer la cotation */}
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12.5 }}>
            {cotation?.generated_at
              ? <span style={{ color: '#059669', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5l10 -10" /></svg>
                  {t('gf.pg_cotation_generated')} {genDate(cotation.generated_at)} · {fmt2(cotation.total_ttc)}
                </span>
              : <span style={{ color: theme.textTertiary }}>{t('gf.pg_cotation_none')}</span>}
          </div>
          {!cotationLocked && (
            <button onClick={() => setShowCotationConfirm(true)} disabled={!canGenerateCotation}
              style={{ fontSize: 13.5, padding: '9px 18px', borderRadius: 8, background: canGenerateCotation ? theme.text : '#E2E2DF', color: canGenerateCotation ? '#fff' : '#9A9A95', border: 'none', cursor: canGenerateCotation ? 'pointer' : 'not-allowed', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3v4a1 1 0 0 0 1 1h4M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2zM9 15l2 2l4 -4" /></svg>
              {cotation?.generated_at ? t('gf.pg_regenerate_cotation') : t('gf.pg_generate_cotation')}
            </button>
          )}
        </div>
      </CollapsibleCard>

      {/* ── Section Variations ── */}
      {bondPeriods.length > 0 && (
        <CollapsibleCard
          defaultOpen={pendingVarDecls.length > 0}
          icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={theme.orange} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6 -6l4 4l8 -8M21 7v4M21 7h-4" /></svg>}
          title={t('gf.pg_variations_title')}
          summary={pendingVarDecls.length > 0
            ? `${pendingVarDecls.length} ${t('gf.pg_var_badge_pending').toLowerCase()} \u00b7 ${fmt2(pendingTot.ttc)}`
            : (variationDecls.length ? `\u2713 ${t('gf.pg_var_appel_none')}` : null)}>
          <div style={{ fontSize: 12, color: theme.textSecondary, marginBottom: 14 }}>{t('gf.pg_variations_hint')}</div>

          {/* Peak bond % par période bond level */}
          <div style={{ fontSize: 11.5, fontWeight: 700, color: theme.textSecondary, marginBottom: 6 }}>{t('gf.pg_var_peakbond_title')}</div>
          <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 14 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: theme.surfaceAlt, borderBottom: `1px solid ${theme.border}` }}>
                  <th style={{ ...th, textAlign: 'left' }}>{t('gf.pg_var_period')}</th>
                  <th style={th}>{t('gf.pg_col_bond')}</th>
                  <th style={th}>{t('gf.pg_var_peakbond')}</th>
                </tr>
              </thead>
              <tbody>
                {peakBondByPeriod.map((p, i) => (
                  <tr key={i} style={{ borderTop: i ? `1px solid ${theme.border}` : 'none' }}>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{monthLabel(startMonth, p.start, lang)} → {monthLabel(startMonth, p.end, lang)}</td>
                    <td style={td}>{fmt0(p.amount)}</td>
                    <td style={{ ...td, fontWeight: 700, color: '#0369A1' }}>{(p.pct * 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Booking Departing : initial vs nouveau (après déclarations validées) */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <ParamPill label={t('gf.pg_var_booking_initial')} value={fmt0(totalBookings1)} />
            <ParamPill label={t('gf.pg_var_booking_new')} value={fmt0(newBookingTotal)} />
            <div style={{ background: bookingIncrease > 0 ? '#ECFDF5' : theme.surfaceAlt, border: `1px solid ${bookingIncrease > 0 ? '#6EE7B7' : theme.border}`, borderRadius: 8, padding: '9px 12px' }}>
              <div style={{ fontSize: 10.5, color: bookingIncrease > 0 ? '#065F46' : theme.textTertiary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('gf.pg_var_increase')}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: bookingIncrease > 0 ? '#059669' : theme.textTertiary, marginTop: 2 }}>{bookingIncrease > 0 ? '+' + fmt0(bookingIncrease) : '—'}</div>
            </div>
          </div>

          {/* Variations issues des déclarations validées en hausse */}
          {variationDecls.length === 0 ? (
            <div style={{ textAlign: 'center', color: theme.textTertiary, fontSize: 12.5, padding: '16px 0', border: `1px dashed ${theme.border}`, borderRadius: 8 }}>
              {t('gf.pg_var_none')}
            </div>
          ) : variationDecls.map(vd => {
            const appel = coveredBy(vd.decl.id);
            return (
              <div key={vd.decl.id} style={{ border: `1px solid ${appel ? '#6EE7B7' : theme.borderStrong}`, borderRadius: 10, padding: 14, marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: theme.text }}>{t('gf.pg_variation_label')} — {vd.label}</div>
                  {appel
                    ? <span style={{ fontSize: 11, fontWeight: 700, color: '#065F46', background: '#D1FAE5', borderRadius: 99, padding: '3px 10px' }}>{t('gf.pg_var_badge_covered')} {genDate(appel.generated_at)}</span>
                    : <span style={{ fontSize: 11, fontWeight: 700, color: '#9A5A00', background: '#FEF3E2', borderRadius: 99, padding: '3px 10px' }}>{t('gf.pg_var_badge_pending')}</span>}
                </div>
                {/* Sous-variations (une par période bond level traversée) */}
                <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                      <thead>
                        <tr style={{ background: theme.surfaceAlt, borderBottom: `1px solid ${theme.border}` }}>
                          <th style={{ ...th, textAlign: 'left' }}>{t('gf.tarif_date_debut')}</th>
                          <th style={{ ...th, textAlign: 'left' }}>{t('gf.tarif_date_fin')}</th>
                          <th style={th}>{t('gf.pg_var_peakbond')}</th>
                          <th style={th}>{t('gf.pg_var_value')}</th>
                          <th style={th}>{t('gf.pg_var_days')}</th>
                          <th style={th}>{t('gf.tarif_prime_ht')}</th>
                          <th style={th}>{t('gf.tarif_prime_ttc')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vd.subs.map((s, i) => (
                          <tr key={i} style={{ borderTop: i ? `1px solid ${theme.border}` : 'none' }}>
                            <td style={{ ...td, textAlign: 'left' }}>{fmtDMY(s.date_debut)}</td>
                            <td style={{ ...td, textAlign: 'left' }}>{fmtDMY(s.date_fin)}</td>
                            <td style={td}>{(s.pct * 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %</td>
                            <td style={{ ...td, fontWeight: 600 }}>{fmt0(s.valeur)}</td>
                            <td style={td}>{s.days}</td>
                            <td style={td}>{fmt2(s.ht)}</td>
                            <td style={{ ...td, fontWeight: 700, color: '#059669' }}>{fmt2(s.ttc)}</td>
                          </tr>
                        ))}
                      </tbody>
                      {vd.subs.length > 1 && (
                        <tfoot>
                          <tr style={{ borderTop: `2px solid ${theme.borderStrong}`, background: '#F0FDF4' }}>
                            <td colSpan={5} style={{ ...td, textAlign: 'left', fontWeight: 800, color: '#065F46' }}>{t('gf.tarif_total')}</td>
                            <td style={{ ...td, fontWeight: 700, color: '#065F46' }}>{fmt2(vd.tot.ht)}</td>
                            <td style={{ ...td, fontWeight: 800, color: '#059669' }}>{fmt2(vd.tot.ttc)}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
                <div style={{ marginTop: 10, fontSize: 12.5, color: theme.textSecondary }}>
                  {t('gf.tarif_prime_ttc')} : <strong>{fmt2(vd.tot.ttc)}</strong>
                </div>
              </div>
            );
          })}

          {/* ── Appel de prime de variation GLOBAL ── */}
          <div style={{ border: `1.5px solid ${pendingVarDecls.length ? '#C2410C' : theme.border}`, borderRadius: 10, padding: 14, marginTop: 4, background: pendingVarDecls.length ? '#FFF7F2' : theme.surfaceAlt }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: theme.text, marginBottom: 6 }}>{t('gf.pg_var_appel_title')}</div>
            {pendingVarDecls.length ? (
              <>
                <div style={{ fontSize: 12.5, color: theme.textSecondary, marginBottom: 10 }}>{t('gf.pg_var_appel_pending_desc')}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13 }}>
                    {pendingVarDecls.map(vd => (
                      <div key={vd.decl.id} style={{ color: theme.textSecondary, padding: '1px 0' }}>· {vd.label} — <strong style={{ color: theme.text }}>{fmt2(vd.tot.ttc)}</strong></div>
                    ))}
                    <div style={{ marginTop: 6, fontSize: 14, fontWeight: 800, color: '#C2410C' }}>{t('gf.tarif_total')} : {fmt2(pendingTot.ttc)}</div>
                  </div>
                  {!readOnly && (
                    <button onClick={() => { setAppelSelection(pendingVarDecls.map(vd => vd.decl.id)); setConfirmAppel(true); }}
                      style={{ fontSize: 13, padding: '10px 18px', borderRadius: 8, background: '#C2410C', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3v4a1 1 0 0 0 1 1h4M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2zM9 15l2 2l4 -4" /></svg>
                      {t('gf.pg_var_appel_generate')}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 12.5, color: theme.textTertiary }}>{t('gf.pg_var_appel_none')}</div>
            )}
            {appelsHistory.length > 0 && (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${theme.border}` }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: theme.textTertiary, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}>{t('gf.pg_var_appel_history')}</div>
                {appelsHistory.map((a, i) => (
                  <div key={i} style={{ fontSize: 12.5, color: '#065F46', display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5l10 -10" /></svg>
                    {genDate(a.generated_at)} · {a.nb_decls || 1} {t('gf.pg_variation_label').toLowerCase()}(s) · <strong>{fmt2(a.total_ttc)}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CollapsibleCard>
      )}

      {/* Tableau — Bond Graph (gauche) + tableau parallèle « déclarations » (droite) */}
      <CollapsibleCard
        defaultOpen={bondCoveredUntil < 12}
        icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={theme.orange} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2zM3 10h18M10 3v18" /></svg>}
        title={t('gf.pg_table_title')}
        summary={`${bondPeriods.length} · ${Math.min(12, bondCoveredUntil)}/12`}>
        {/* Sous-section : récap des périodes de bond level */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 12px', background: theme.surfaceAlt, border: `1px solid ${theme.border}`, borderRadius: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: '.03em' }}>{t('gf.pg_bond_periods_recap')}</span>
          {bondPeriods.length === 0 && <span style={{ fontSize: 12.5, color: theme.textTertiary }}>{t('gf.pg_bond_periods_none')}</span>}
          {bondPeriods.map((p, i) => (
            <span key={i} style={{ fontSize: 12, fontWeight: 600, color: '#9A5A00', background: '#FEF3E2', border: '1px solid #FCD9A6', borderRadius: 99, padding: '3px 11px' }}>
              {monthLabel(startMonth, p.start, lang)} → {monthLabel(startMonth, p.end, lang)} · {fmt0(parseFloat(p.amount) || 0)}
            </span>
          ))}
          {nextBondMonth >= 0 && bondPeriods.length > 0 && (
            <span style={{ fontSize: 11.5, color: theme.textTertiary }}>{t('gf.pg_bond_periods_incomplete')}</span>
          )}
          {!cotationLocked && bondPeriods.length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
              <button onClick={removeLastBondPeriod} style={{ fontSize: 11.5, padding: '4px 10px', borderRadius: 6, background: '#fff', color: '#C2410C', border: '1px solid #FCD9A6', cursor: 'pointer', fontWeight: 600 }}>{t('gf.pg_bond_remove_last')}</button>
              <button onClick={resetBondPeriods} style={{ fontSize: 11.5, padding: '4px 10px', borderRadius: 6, background: '#fff', color: theme.textSecondary, border: `1px solid ${theme.border}`, cursor: 'pointer', fontWeight: 600 }}>{t('gf.pg_bond_reset')}</button>
            </div>
          )}
        </div>
        <div style={{ border: `1px solid ${theme.borderStrong}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1180 }}>
            <thead>
              {/* Super-en-têtes : payment graph original | tableau parallèle déclarations */}
              <tr style={{ background: theme.surfaceAlt }}>
                <th style={{ ...th }} />
                <th colSpan={7} style={{ ...th, textAlign: 'center', color: theme.textTertiary, borderBottom: `1px solid ${theme.border}` }}>{t('gf.pg_group_original')}</th>
                <th colSpan={2} style={{ ...th, textAlign: 'center', color: '#185FA5', background: PAR_TINT, borderLeft: PAR_BORDER, borderBottom: `1px solid ${theme.border}` }}>{t('gf.pg_group_declarations')}</th>
              </tr>
              <tr style={{ background: theme.surfaceAlt, borderBottom: `1px solid ${theme.borderStrong}` }}>
                <th style={{ ...th, textAlign: 'left' }}>{t('gf.pg_col_month')}</th>
                <th style={th}>{t('gf.pg_col_bookings')}</th>
                <th style={th}>{t('gf.pg_col_cash_received')}</th>
                <th style={th}>{t('gf.pg_col_cash_held')}</th>
                <th style={{ ...th, color: '#C2410C' }}>{t('gf.pg_col_exposure')}</th>
                <th style={th}>{t('gf.pg_col_last_bond')}</th>
                <th style={{ ...th, textAlign: 'center', minWidth: 110 }}>{t('gf.pg_col_bond')}</th>
                <th style={th}>{t('gf.pg_col_premium')}</th>
                <th style={{ ...th, background: PAR_TINT, color: '#185FA5', borderLeft: PAR_BORDER }}>{t('gf.pg_col_declarations')}</th>
                <th style={{ ...th, background: PAR_TINT, color: '#0369A1' }}>{t('gf.pg_col_updated_exposure')}</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: N_MONTHS }, (_, m) => {
                const y2 = m >= 12;
                const rowBg = y2 ? '#FBFBFA' : (m % 2 === 0 ? '#fff' : '#FCFCFB');
                const dim = y2 ? 0.5 : 1;
                const ym = ymAt(startMonth, m);
                const realise = (!y2 && ym) ? realiseByMonth[ym] : undefined;
                const sep = (!y2 && ym) ? periodByEndMonth[ym] : null;
                return (
                  <React.Fragment key={m}>
                    <tr style={{ borderTop: `1px solid ${theme.border}`, background: rowBg, opacity: dim }}>
                      <td style={{ ...td, textAlign: 'left', fontWeight: 600, color: y2 ? theme.textTertiary : theme.text }}>
                        {monthLabel(startMonth, m, lang)}
                        {m === 0 && <span style={{ marginLeft: 6, fontSize: 9.5, color: theme.orange, fontWeight: 700 }}>{t('gf.pg_year1_tag')}</span>}
                        {m === 12 && <span style={{ marginLeft: 6, fontSize: 9.5, color: theme.textTertiary, fontWeight: 700 }}>{t('gf.pg_year2_tag')}</span>}
                      </td>
                      <td style={{ ...td, color: theme.textSecondary }}>{fmt0(model.Cfull[m])}</td>
                      <td style={{ ...td, color: theme.textSecondary }}>{fmt0(model.B[m])}</td>
                      <td style={{ ...td, color: theme.textSecondary }}>{fmt0(model.D[m])}</td>
                      <td style={{ ...td, fontWeight: 700, color: '#C2410C' }}>{fmt0(model.F[m])}</td>
                      <td style={{ ...td, color: theme.textTertiary }}>{lastYearBond[m] != null && lastYearBond[m] !== '' ? fmt0(parseFloat(lastYearBond[m])) : '—'}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                        {y2 ? (
                          <span style={{ fontSize: 12.5, color: theme.textTertiary }}>—</span>
                        ) : (bondLevels[m] !== '' && bondLevels[m] != null) ? (
                          <span style={{ fontSize: 12.5, fontWeight: 700, color: theme.text }}>{fmt0(parseFloat(bondLevels[m]))}</span>
                        ) : (m === nextBondMonth && !cotationLocked) ? (
                          <button onClick={openBondModal}
                            style={{ fontSize: 11, padding: '5px 9px', borderRadius: 6, background: theme.orange, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
                            {t('gf.pg_define_bond')}
                          </button>
                        ) : (
                          <span style={{ fontSize: 12.5, color: theme.textTertiary }}>—</span>
                        )}
                      </td>
                      <td style={{ ...td, fontWeight: 600, color: y2 ? theme.textTertiary : (premiumMonthly[m] > 0 ? '#059669' : theme.textTertiary) }}>
                        {y2 ? '—' : (premiumMonthly[m] > 0 ? fmt2(premiumMonthly[m]) : '—')}
                      </td>
                      {/* ── Tableau parallèle : Declarations · Updated exposure ── */}
                      <td style={{ ...td, background: PAR_TINT, borderLeft: PAR_BORDER, color: realise != null ? '#185FA5' : theme.textTertiary, fontWeight: realise != null ? 700 : 400 }}>
                        {realise != null ? fmt0(realise) : '—'}
                      </td>
                      <td style={{ ...td, background: PAR_TINT, fontWeight: 700, color: y2 ? theme.textTertiary : '#0369A1' }}>
                        {y2 ? '—' : fmt0(modelUpd.F[m])}
                      </td>
                    </tr>
                    {sep && (
                      <tr style={{ background: sep.over ? '#FFFBEB' : '#F8FAFC', borderTop: `1px solid ${theme.border}` }}>
                        <td colSpan={10} style={{ padding: '8px 12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ fontSize: 12 }}>
                              <strong style={{ color: theme.text }}>{t('gf.pg_period_declared')} {monthLabel(sep.start_month, 0, lang)} → {monthLabel(sep.end_month, 0, lang)}</strong>
                              <span style={{ marginLeft: 10, color: theme.textSecondary }}>
                                {t('gf.pg_col_declarations')} <strong style={{ color: sep.over ? '#0F6E56' : theme.text }}>{fmt0(sep.totalReal)}</strong> · {t('gf.pg_period_vs')} {fmt0(sep.totalPrev)}
                              </span>
                              {sep.over && (
                                <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 700, color: '#0F6E56', background: '#E1F5EE', borderRadius: 99, padding: '2px 8px' }}>
                                  {t('gf.pg_over_forecast')}
                                </span>
                              )}
                            </div>
                            {sep.over && (
                              <span style={{ fontSize: 11, color: theme.textTertiary, fontStyle: 'italic' }}>{t('gf.pg_variation_see_section')}</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: `2px solid ${theme.borderStrong}`, background: '#F0FDF4' }}>
                <td style={{ ...td, textAlign: 'left', fontWeight: 800, color: '#065F46' }}>{t('gf.pg_total_year1')}</td>
                <td style={{ ...td, fontWeight: 700, color: '#065F46' }}>{fmt0(totalBookings1)}</td>
                <td colSpan={2} />
                <td style={{ ...td, fontWeight: 800, color: '#C2410C' }}>{fmt0(peakExposure1)}</td>
                <td />
                <td style={{ ...td, textAlign: 'center', fontSize: 10.5, color: '#065F46', fontWeight: 700 }}>{t('gf.pg_annual_ht')}</td>
                <td style={{ ...td, fontWeight: 800, color: '#059669' }}>{fmt2(annualHT)}</td>
                <td style={{ ...td, background: PAR_TINT, borderLeft: PAR_BORDER, fontWeight: 700, color: '#185FA5' }}>{totalRealise1 > 0 ? fmt0(totalRealise1) : ''}</td>
                <td style={{ ...td, background: PAR_TINT, fontWeight: 800, color: '#0369A1' }}>{hasDeclarations ? fmt0(peakUpdated) : ''}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        </div>
      </CollapsibleCard>

      {/* Graphique — courbes du bond graph (comme l'Excel) */}
      <div style={{ ...card, marginBottom: 14 }}>
        <div style={cardTitle}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={theme.orange} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19l4 -6l4 2l4 -5l4 4M4 5v14h16" />
          </svg>
          {t('gf.pg_chart_title')}
        </div>
        <BondChart
          F={model.F} FUpd={modelUpd.F}
          bonds={bondLevels} lastBond={lastYearBond}
          startMonth={startMonth} lang={lang}
          showUpd={hasDeclarations} t={t}
        />
      </div>

      {/* Rappel des formules */}
      <div style={{ background: '#EFF6FF', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#1D4ED8',
        display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 6 }}>
        <span>📥 <strong>Cash Received</strong> = {t('gf.pg_formula_cash')}</span>
        <span>🛡 <strong>Exposure</strong> = max(Bookings, Cash Held)</span>
        <span>💶 <strong>Premium</strong> = {t('gf.pg_formula_premium')}</span>
      </div>
      <div style={{ fontSize: 11, color: theme.textTertiary }}>{t('gf.pg_last_bond_hint')}</div>

      {/* Modale — Définir bond level (période) */}
      {bondModal && (
        <div onClick={() => setBondModal(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, width: 440, maxWidth: '92vw' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: theme.text }}>{t('gf.pg_bond_modal_title')}</div>
            <div style={{ fontSize: 13, color: theme.textSecondary, lineHeight: 1.55, marginBottom: 16 }}>{t('gf.pg_bond_modal_desc')}</div>

            {/* Période : début fixe (1er mois non couvert) → fin au choix */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={smallLbl}>{t('gf.pg_bond_period_start')}</label>
                <div style={{ ...lockedInp, padding: '8px 10px' }}>{nextBondMonth >= 0 ? monthLabel(startMonth, nextBondMonth, lang) : '—'}</div>
              </div>
              <div>
                <label style={smallLbl}>{t('gf.pg_bond_period_end')}</label>
                <select value={bondModal.end} onChange={e => setBondModal({ ...bondModal, end: parseInt(e.target.value, 10) })}
                  style={{ ...numInp, textAlign: 'left' }}>
                  {Array.from({ length: 12 - nextBondMonth }, (_, k) => nextBondMonth + k).map(off => (
                    <option key={off} value={off}>{monthLabel(startMonth, off, lang)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Montant du bond level OU Exposure Max */}
            <label style={smallLbl}>{t('gf.pg_bond_amount')}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <input type="number" value={bondModal.expoMax ? peakExposure1 : bondModal.amount} disabled={bondModal.expoMax} min="0" step="1000"
                onChange={e => setBondModal({ ...bondModal, amount: e.target.value })} placeholder="0"
                style={{ ...(bondModal.expoMax ? lockedInp : numInp), flex: 1, textAlign: 'left' }} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: theme.textSecondary, cursor: 'pointer', marginBottom: 20 }}>
              <input type="checkbox" checked={bondModal.expoMax} onChange={e => setBondModal({ ...bondModal, expoMax: e.target.checked })} />
              {t('gf.pg_bond_expo_max')} <strong style={{ color: theme.text }}>{fmt0(peakExposure1)}</strong>
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setBondModal(null)}
                style={{ fontSize: 14, padding: '8px 16px', borderRadius: 8, border: `1px solid ${theme.border}`, background: '#fff', cursor: 'pointer' }}>{t('common.cancel')}</button>
              <button onClick={confirmBondModal} disabled={!bondModal.expoMax && !(parseFloat(bondModal.amount) > 0)}
                style={{ fontSize: 14, padding: '8px 18px', borderRadius: 8, background: (!bondModal.expoMax && !(parseFloat(bondModal.amount) > 0)) ? '#E2E2DF' : theme.orange, color: (!bondModal.expoMax && !(parseFloat(bondModal.amount) > 0)) ? '#9A9A95' : '#fff', border: 'none', cursor: (!bondModal.expoMax && !(parseFloat(bondModal.amount) > 0)) ? 'not-allowed' : 'pointer', fontWeight: 700 }}>{t('gf.pg_bond_modal_confirm')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modale — génération de cotation */}
      {showCotationConfirm && (
        <div onClick={() => setShowCotationConfirm(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, width: 460, maxWidth: '92vw' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: theme.text }}>{t('gf.pg_cotation_confirm_title')}</div>
            <div style={{ fontSize: 13.5, color: theme.textSecondary, lineHeight: 1.6, marginBottom: 18 }}>{t('gf.pg_cotation_confirm_desc')}</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setShowCotationConfirm(false)}
                style={{ fontSize: 14, padding: '8px 16px', borderRadius: 8, border: `1px solid ${theme.border}`, background: '#fff', cursor: 'pointer' }}>{t('common.cancel')}</button>
              <button onClick={doGenerateCotation}
                style={{ fontSize: 14, padding: '8px 18px', borderRadius: 8, background: '#059669', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700 }}>{t('gf.pg_cotation_confirm_btn')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modale — génération de L'appel de prime de variation (global) */}
      {confirmAppel && (
        <div onClick={() => setConfirmAppel(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, width: 480, maxWidth: '92vw' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: theme.text }}>{t('gf.pg_var_appel_confirm_title')}</div>
            <div style={{ fontSize: 13.5, color: theme.textSecondary, lineHeight: 1.6, marginBottom: 12 }}>{t('gf.pg_var_appel_confirm_desc')}</div>
            <div style={{ background: '#F8FAFC', border: `1px solid ${theme.border}`, borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
              {pendingVarDecls.map(vd => {
                const checked = appelSelection.includes(vd.decl.id);
                return (
                  <label key={vd.decl.id} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, color: checked ? '#334155' : theme.textTertiary, padding: '4px 0', cursor: 'pointer' }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleAppelSel(vd.decl.id)} style={{ accentColor: '#C2410C', flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>{vd.label}</span>
                    <strong style={{ textDecoration: checked ? 'none' : 'line-through' }}>{fmt2(vd.tot.ttc)}</strong>
                  </label>
                );
              })}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, fontWeight: 800, color: '#C2410C', borderTop: `1px solid ${theme.border}`, marginTop: 6, paddingTop: 6 }}>
                <span>{t('gf.tarif_total')} ({selectedVarDecls.length}/{pendingVarDecls.length})</span><span>{fmt2(selectedTot.ttc)}</span>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setConfirmAppel(false)}
                style={{ fontSize: 14, padding: '8px 16px', borderRadius: 8, border: `1px solid ${theme.border}`, background: '#fff', cursor: 'pointer' }}>{t('common.cancel')}</button>
              <button onClick={doGenerateAppel} disabled={!selectedVarDecls.length}
                style={{ fontSize: 14, padding: '8px 18px', borderRadius: 8, background: '#C2410C', color: '#fff', border: 'none', cursor: selectedVarDecls.length ? 'pointer' : 'not-allowed', opacity: selectedVarDecls.length ? 1 : .5, fontWeight: 700 }}>{t('gf.pg_var_appel_confirm_btn')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
